import { AppLayout } from "@/components/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Shield, Cookie, Scale, Code } from "lucide-react";

const Legal = () => {
  return (
    <AppLayout title="Mentions légales">
      <div className="p-6 max-w-4xl">
        <Tabs defaultValue="terms" className="space-y-6">
          <TabsList className="bg-secondary rounded-xl p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="terms" className="data-[state=active]:bg-card rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              CGU
            </TabsTrigger>
            <TabsTrigger value="privacy" className="data-[state=active]:bg-card rounded-lg">
              <Shield className="w-4 h-4 mr-2" />
              Confidentialité
            </TabsTrigger>
            <TabsTrigger value="cookies" className="data-[state=active]:bg-card rounded-lg">
              <Cookie className="w-4 h-4 mr-2" />
              Cookies
            </TabsTrigger>
            <TabsTrigger value="legal" className="data-[state=active]:bg-card rounded-lg">
              <Scale className="w-4 h-4 mr-2" />
              Mentions légales
            </TabsTrigger>
            <TabsTrigger value="api" className="data-[state=active]:bg-card rounded-lg">
              <Code className="w-4 h-4 mr-2" />
              API
            </TabsTrigger>
          </TabsList>

          {/* CGU */}
          <TabsContent value="terms" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 prose prose-invert max-w-none">
              <h1 className="text-2xl font-bold text-foreground mb-6">Conditions Générales d'Utilisation</h1>
              <p className="text-muted-foreground mb-4">Dernière mise à jour : 3 décembre 2024</p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. Objet</h2>
              <p className="text-muted-foreground mb-4">
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du service Floop, 
                une plateforme de génération et de retouche d'images par intelligence artificielle.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Acceptation des conditions</h2>
              <p className="text-muted-foreground mb-4">
                En accédant ou en utilisant Floop, vous acceptez d'être lié par les présentes CGU. 
                Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre service.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. Description du service</h2>
              <p className="text-muted-foreground mb-4">
                Floop est un service de génération d'images basé sur notre modèle d'intelligence artificielle. 
                Le service comprend :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Génération d'images à partir de descriptions textuelles (Floop Basic)</li>
                <li>Retouche et transformation d'images avancées (Floop Pro)</li>
                <li>Stockage temporaire des images générées</li>
                <li>Historique des générations</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Inscription et compte</h2>
              <p className="text-muted-foreground mb-4">
                Pour utiliser Floop, vous devez créer un compte en fournissant des informations exactes et complètes. 
                Vous êtes responsable de la confidentialité de vos identifiants et de toutes les activités sur votre compte.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Système de crédits</h2>
              <p className="text-muted-foreground mb-4">
                L'utilisation de Floop fonctionne sur un système de crédits :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Les crédits sont consommés lors de chaque génération d'image</li>
                <li>Les crédits achetés sont non remboursables sauf disposition légale contraire</li>
                <li>Les crédits non utilisés restent valables pendant 12 mois</li>
                <li>Le coût en crédits varie selon le modèle et la résolution utilisés</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Propriété intellectuelle</h2>
              <p className="text-muted-foreground mb-4">
                Les images générées via Floop vous appartiennent. Vous pouvez les utiliser à des fins personnelles et commerciales 
                sous réserve du respect des lois applicables et des droits des tiers. Floop conserve les droits sur sa technologie, 
                son interface et sa marque.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. Utilisation acceptable</h2>
              <p className="text-muted-foreground mb-4">Il est interdit d'utiliser Floop pour :</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Générer du contenu illégal, diffamatoire ou portant atteinte aux droits d'autrui</li>
                <li>Créer des deepfakes malveillants ou du contenu trompeur</li>
                <li>Générer du contenu à caractère sexuel impliquant des mineurs</li>
                <li>Contourner les mesures de sécurité du service</li>
                <li>Revendre ou redistribuer l'accès au service</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">8. Limitation de responsabilité</h2>
              <p className="text-muted-foreground mb-4">
                Floop est fourni "tel quel". Nous ne garantissons pas l'exactitude, la qualité ou l'adéquation 
                des images générées à vos besoins spécifiques. Notre responsabilité est limitée au montant 
                des crédits achetés au cours des 12 derniers mois.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">9. Modification des CGU</h2>
              <p className="text-muted-foreground mb-4">
                Nous nous réservons le droit de modifier ces CGU à tout moment. Les modifications seront notifiées 
                par email ou via l'interface du service. La poursuite de l'utilisation après notification vaut acceptation.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">10. Droit applicable</h2>
              <p className="text-muted-foreground mb-4">
                Les présentes CGU sont régies par le droit français. Tout litige sera soumis aux tribunaux compétents de Paris.
              </p>
            </div>
          </TabsContent>

          {/* Privacy Policy */}
          <TabsContent value="privacy" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 prose prose-invert max-w-none">
              <h1 className="text-2xl font-bold text-foreground mb-6">Politique de Confidentialité</h1>
              <p className="text-muted-foreground mb-4">Dernière mise à jour : 3 décembre 2024</p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. Collecte des données</h2>
              <p className="text-muted-foreground mb-4">Nous collectons les données suivantes :</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Informations de compte : email, nom d'utilisateur, mot de passe (chiffré)</li>
                <li>Données d'utilisation : prompts, images générées, préférences</li>
                <li>Données de paiement : traitées par notre partenaire de paiement sécurisé</li>
                <li>Données techniques : adresse IP, type de navigateur, données de connexion</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Utilisation des données</h2>
              <p className="text-muted-foreground mb-4">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Fournir et améliorer nos services</li>
                <li>Gérer votre compte et vos crédits</li>
                <li>Vous contacter concernant votre compte ou nos services</li>
                <li>Prévenir la fraude et assurer la sécurité</li>
                <li>Respecter nos obligations légales</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. Conservation des données</h2>
              <p className="text-muted-foreground mb-4">
                Les images générées sont conservées pendant 30 jours dans votre historique. 
                Les données de compte sont conservées jusqu'à suppression du compte plus 3 ans pour obligations légales.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Partage des données</h2>
              <p className="text-muted-foreground mb-4">
                Nous ne vendons pas vos données personnelles. Nous pouvons partager vos données avec :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Nos sous-traitants techniques (hébergement, paiement)</li>
                <li>Les autorités si requis par la loi</li>
              </ul>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Vos droits (RGPD)</h2>
              <p className="text-muted-foreground mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Droit d'accès à vos données personnelles</li>
                <li>Droit de rectification des données inexactes</li>
                <li>Droit à l'effacement ("droit à l'oubli")</li>
                <li>Droit à la portabilité de vos données</li>
                <li>Droit d'opposition au traitement</li>
                <li>Droit de retirer votre consentement</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Pour exercer ces droits, contactez-nous à : privacy@floop.ai
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Sécurité</h2>
              <p className="text-muted-foreground mb-4">
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données :
                chiffrement SSL/TLS, stockage sécurisé, accès restreint, audits réguliers.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. Contact DPO</h2>
              <p className="text-muted-foreground mb-4">
                Pour toute question relative à vos données personnelles, contactez notre Délégué à la Protection des Données : dpo@floop.ai
              </p>
            </div>
          </TabsContent>

          {/* Cookies */}
          <TabsContent value="cookies" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 prose prose-invert max-w-none">
              <h1 className="text-2xl font-bold text-foreground mb-6">Politique de Cookies</h1>
              <p className="text-muted-foreground mb-4">Dernière mise à jour : 3 décembre 2024</p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
              <p className="text-muted-foreground mb-4">
                Un cookie est un petit fichier texte stocké sur votre appareil lors de votre visite sur notre site. 
                Les cookies nous permettent de vous reconnaître et de personnaliser votre expérience.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Types de cookies utilisés</h2>
              
              <h3 className="text-lg font-medium text-foreground mt-6 mb-3">Cookies essentiels</h3>
              <p className="text-muted-foreground mb-4">
                Nécessaires au fonctionnement du site : authentification, sécurité, préférences de session. 
                Ces cookies ne peuvent pas être désactivés.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6 mb-3">Cookies analytiques</h3>
              <p className="text-muted-foreground mb-4">
                Nous permettent de comprendre comment vous utilisez Floop pour améliorer nos services. 
                Les données sont anonymisées.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6 mb-3">Cookies de préférences</h3>
              <p className="text-muted-foreground mb-4">
                Mémorisent vos préférences (langue, thème) pour personnaliser votre expérience.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. Gestion des cookies</h2>
              <p className="text-muted-foreground mb-4">
                Vous pouvez gérer vos préférences de cookies à tout moment via les paramètres de votre navigateur 
                ou via notre bandeau de consentement. La désactivation de certains cookies peut affecter votre expérience.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Durée de conservation</h2>
              <p className="text-muted-foreground mb-4">
                Les cookies de session sont supprimés à la fermeture du navigateur. Les cookies persistants 
                ont une durée maximale de 13 mois conformément aux recommandations de la CNIL.
              </p>
            </div>
          </TabsContent>

          {/* Legal Mentions */}
          <TabsContent value="legal" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 prose prose-invert max-w-none">
              <h1 className="text-2xl font-bold text-foreground mb-6">Mentions Légales</h1>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">Éditeur du site</h2>
              <p className="text-muted-foreground mb-4">
                Floop SAS<br />
                Capital social : 10 000€<br />
                RCS Paris B 123 456 789<br />
                Siège social : 42 Rue de l'Innovation, 75001 Paris, France<br />
                Email : contact@floop.ai<br />
                Directeur de la publication : [Nom du directeur]
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">Hébergement</h2>
              <p className="text-muted-foreground mb-4">
                Le site est hébergé sur une infrastructure cloud sécurisée.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">Propriété intellectuelle</h2>
              <p className="text-muted-foreground mb-4">
                L'ensemble du contenu du site Floop (textes, images, logos, marques, design, code source) est protégé 
                par les droits de propriété intellectuelle. Toute reproduction ou représentation, même partielle, 
                est interdite sans autorisation écrite préalable.
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">Crédits</h2>
              <p className="text-muted-foreground mb-4">
                Design et développement : Équipe Floop<br />
                Illustrations : Générées par Floop AI
              </p>

              <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">Contact</h2>
              <p className="text-muted-foreground mb-4">
                Pour toute question concernant ce site :<br />
                Email : legal@floop.ai<br />
                Adresse : 42 Rue de l'Innovation, 75001 Paris, France
              </p>
            </div>
          </TabsContent>

          {/* API */}
          <TabsContent value="api" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 mx-auto bg-primary/20 rounded-2xl flex items-center justify-center">
                  <Code className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">API Floop</h1>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Intégrez la puissance de Floop directement dans vos applications. 
                  Notre API RESTful vous permet de générer des images par programmation.
                </p>
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-highlight/20 text-highlight rounded-xl font-semibold">
                  🚀 Coming Soon
                </div>
                <p className="text-sm text-muted-foreground">
                  L'API Floop sera bientôt disponible. Inscrivez-vous pour être notifié du lancement.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Legal;
